import type { Problem } from '@/types';

/* Practice Gym ladder: Funnel Analysis (module m9). Problem-forward, progressive.
   Auto-included via src/data/gym/index.ts; referenced by the `fun` ladder. */
export const funnelProblems: Problem[] = [
  {
    "id": "fu1",
    "ladder": "fun",
    "pos": 1,
    "stage": "Recognition",
    "lvl": 0,
    "difficulty": "recognition",
    "priority": "required",
    "source": "interview-style",
    "module": "m9",
    "title": "What is the funnel denominator?",
    "concept": ["funnel"],
    "obj": ["Customer", "Charge"],
    "metric": "n/a",
    "edge": ["denominator"],
    "timed": false,
    "est": "3 min",
    "business": "A PM asks for the checkout 'conversion rate' but it's unclear which conversion.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "No SQL yet. In plain English: for exposed → attempted → succeeded, name the three rates and the denominator each uses, and why you count customers not charge events.",
    "deliverable": "A short explanation an interviewer could follow: the three rates, their denominators, and the distinct-entity rule.",
    "why": "Every funnel question is ambiguous until you fix the entity and the denominator; recognise this before writing SQL.",
    "harder": "First rung — name the entities, steps, and denominators.",
    "before": [
      "A funnel population can only shrink: exposed ≥ attempted ≥ succeeded.",
      "Why a customer's retries must not each count as an attempt."
    ],
    "hints": [
      "attempt_rate = attempted ÷ exposed; success_from_attempt = succeeded ÷ attempted; success_from_exposure = succeeded ÷ exposed.",
      "Count DISTINCT customer_id per step so retries collapse to one."
    ],
    "model": "Three rates from the same counts: attempt_rate = attempted/exposed, success_from_exposure = succeeded/exposed, success_from_attempt = succeeded/attempted. Each names a different denominator, so 'conversion rate' must specify which. You count distinct customers, not charge rows — a customer who retries five times is still one attempt — so attempted ≤ exposed always holds.",
    "grain": "Conceptual — no output rows.",
    "confusion": "Counting charge events lets attempted exceed exposed and a rate exceed 100%.",
    "explain": "Name the three denominators and the distinct-customer rule aloud.",
    "teaches": "Recognising funnel steps, denominators, and entity counting.",
    "mode": "SQL"
  },
  {
    "id": "fu2",
    "ladder": "fun",
    "pos": 2,
    "stage": "Mechanical",
    "lvl": 1,
    "difficulty": "easy",
    "priority": "required",
    "source": "pgexercises-style",
    "module": "m9",
    "title": "Count distinct customers who attempted",
    "concept": ["funnel"],
    "obj": ["Customer", "Charge"],
    "metric": "count",
    "edge": ["distinct"],
    "timed": false,
    "est": "4 min",
    "business": "You need the number of customers who made at least one charge attempt — not the number of charges.",
    "schema": ["charges"],
    "prompt": "Return the count of distinct customers who have any charge, and separately the total number of charge rows, so the difference is visible.",
    "deliverable": "One row: customers, charge_rows.",
    "why": "Distinct-entity counting is the mechanical core of every funnel step.",
    "harder": "Introduces COUNT(DISTINCT …) vs COUNT(*) for funnel steps.",
    "before": ["A customer with many charges should count once toward 'attempted'."],
    "hints": [
      "COUNT(DISTINCT customer_id) for customers.",
      "COUNT(*) for raw charge rows — they differ because of retries."
    ],
    "solution": "SELECT COUNT(DISTINCT customer_id) AS customers,\n       COUNT(*) AS charge_rows\nFROM charges;",
    "grain": "A single summary row.",
    "confusion": "COUNT(*) counts events; COUNT(DISTINCT customer_id) counts entities.",
    "explain": "Explain why a funnel step needs the distinct count.",
    "teaches": "COUNT(DISTINCT entity) as the funnel step primitive.",
    "mode": "SQL"
  },
  {
    "id": "fu3",
    "ladder": "fun",
    "pos": 3,
    "stage": "Simple application",
    "lvl": 2,
    "difficulty": "easy",
    "priority": "required",
    "source": "Mode-style",
    "module": "m9",
    "title": "Exposure → attempt → success counts",
    "concept": ["funnel"],
    "obj": ["Customer", "Charge"],
    "metric": "step counts",
    "edge": ["distinct"],
    "timed": false,
    "est": "6 min",
    "business": "For the 'checkout_v2' experiment, report how many exposed customers attempted and succeeded.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "Return exposed, attempted (distinct exposed customers with a charge), and succeeded (distinct exposed customers with a succeeded charge).",
    "deliverable": "One row: exposed, attempted, succeeded.",
    "why": "Building the three step counts on a real exposed base is the funnel skeleton.",
    "harder": "Joins charges back to an exposed base and counts distinct per step.",
    "before": ["Base = distinct customers exposed to checkout_v2.", "Each step counts distinct customers from that base."],
    "hints": [
      "Exposed CTE: DISTINCT customer_id WHERE experiment='checkout_v2'.",
      "Attempted/succeeded: DISTINCT customer_id from charges joined to exposed."
    ],
    "solution": "WITH exposed AS (\n  SELECT DISTINCT customer_id FROM experiment_exposures WHERE experiment='checkout_v2'\n),\nattempted AS (\n  SELECT DISTINCT c.customer_id FROM charges c JOIN exposed e USING (customer_id)\n),\nsucceeded AS (\n  SELECT DISTINCT c.customer_id FROM charges c JOIN exposed e USING (customer_id) WHERE c.status='succeeded'\n)\nSELECT (SELECT COUNT(*) FROM exposed)   AS exposed,\n       (SELECT COUNT(*) FROM attempted) AS attempted,\n       (SELECT COUNT(*) FROM succeeded) AS succeeded;",
    "verify": {
      "grain": "A single summary row.",
      "columns": ["exposed", "attempted", "succeeded"],
      "sample": { "cols": ["exposed", "attempted", "succeeded"], "rows": [["1000", "700", "560"]] },
      "commonWrong": ["Counting charge events for attempted.", "Attempting/succeeding customers not restricted to the exposed base."],
      "validation": ["exposed ≥ attempted ≥ succeeded"],
      "edgeCases": ["A customer who charged but was never exposed must not count."],
      "checklist": ["exposed base", "distinct per step", "steps restricted to base"]
    },
    "confusion": "Restrict attempt/success to the exposed base, or you'll count unrelated customers.",
    "explain": "State the base and that each step is a distinct subset of it.",
    "teaches": "Assembling the three funnel step counts from an exposed base.",
    "mode": "SQL"
  },
  {
    "id": "fu4",
    "ladder": "fun",
    "pos": 4,
    "stage": "Applied metric",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "required",
    "source": "DataLemur-style",
    "module": "m9",
    "title": "Attempt rate and success rate",
    "concept": ["funnel"],
    "obj": ["Customer", "Charge"],
    "metric": "conversion rates",
    "edge": ["denominator", "zero denominator"],
    "timed": false,
    "est": "7 min",
    "business": "Product wants the checkout_v2 attempt rate and the step success rate, each clearly defined.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "Extend the step counts to also return attempt_rate (vs exposed) and success_from_attempt (vs attempted), guarded.",
    "deliverable": "One row: exposed, attempted, succeeded, attempt_rate, success_from_attempt.",
    "why": "Turning counts into correctly-denominated rates is the funnel payoff and the usual interview deliverable.",
    "harder": "Adds two rates with different, explicitly-named denominators.",
    "before": ["attempt_rate uses exposed; success_from_attempt uses attempted.", "Guard each divide with NULLIF."],
    "hints": [
      "Reuse the exposed/attempted/succeeded CTEs.",
      "ROUND(attempted/exposed,4) and ROUND(succeeded/attempted,4) with numeric casts and NULLIF."
    ],
    "solution": "WITH exposed AS (\n  SELECT DISTINCT customer_id FROM experiment_exposures WHERE experiment='checkout_v2'\n),\nattempted AS (\n  SELECT DISTINCT c.customer_id FROM charges c JOIN exposed e USING (customer_id)\n),\nsucceeded AS (\n  SELECT DISTINCT c.customer_id FROM charges c JOIN exposed e USING (customer_id) WHERE c.status='succeeded'\n)\nSELECT (SELECT COUNT(*) FROM exposed)   AS exposed,\n       (SELECT COUNT(*) FROM attempted) AS attempted,\n       (SELECT COUNT(*) FROM succeeded) AS succeeded,\n       ROUND((SELECT COUNT(*) FROM attempted)::numeric / NULLIF((SELECT COUNT(*) FROM exposed),0), 4)   AS attempt_rate,\n       ROUND((SELECT COUNT(*) FROM succeeded)::numeric / NULLIF((SELECT COUNT(*) FROM attempted),0), 4) AS success_from_attempt;",
    "verify": {
      "grain": "A single summary row.",
      "columns": ["exposed", "attempted", "succeeded", "attempt_rate", "success_from_attempt"],
      "sample": { "cols": ["exposed", "attempted", "succeeded", "attempt_rate", "success_from_attempt"], "rows": [["1000", "700", "560", "0.7000", "0.8000"]] },
      "commonWrong": ["Using exposed as the denominator for the step success rate.", "Integer division without a numeric cast."],
      "validation": ["both rates ∈ [0,1]", "attempted ≤ exposed and succeeded ≤ attempted"],
      "edgeCases": ["Zero exposed → rates NULL via NULLIF, not an error."],
      "checklist": ["attempt_rate vs exposed", "success vs attempted", "guards", "rounded"]
    },
    "confusion": "Name each denominator: success_from_attempt is succeeded/attempted, not succeeded/exposed.",
    "explain": "Say which denominator each rate uses before writing it.",
    "teaches": "Correctly-denominated funnel conversion rates.",
    "mode": "SQL"
  },
  {
    "id": "fu5",
    "ladder": "fun",
    "pos": 5,
    "stage": "Multi-step",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "should",
    "source": "Mode-style",
    "module": "m9",
    "title": "Checkout funnel by variant",
    "concept": ["funnel"],
    "obj": ["Customer", "Charge"],
    "metric": "funnel by variant",
    "edge": ["denominator", "contamination"],
    "timed": false,
    "est": "9 min",
    "business": "Compare checkout_v2 variants: per variant, the exposed → succeeded funnel.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "Per variant, return exposed, succeeded (distinct exposed customers with a succeeded charge), and success_from_exposure.",
    "deliverable": "One row per variant: variant, exposed, succeeded, success_from_exposure.",
    "why": "Per-variant funnels are how experiments are read; the grain and denominator must stay per variant.",
    "harder": "Carries the variant key through each step and groups by it.",
    "before": ["Carry variant into the exposed CTE.", "Each step counts distinct customers within a variant."],
    "howto": [
      "exposed CTE: DISTINCT customer_id, variant for checkout_v2.",
      "succeeded CTE: distinct exposed customers (with variant) who have a succeeded charge.",
      "LEFT JOIN succeeded to exposed on (variant, customer_id); GROUP BY variant."
    ],
    "hints": [
      "COUNT(DISTINCT e.customer_id) per variant for exposed.",
      "LEFT JOIN keeps a variant whose customers never succeeded (rate 0)."
    ],
    "solution": "WITH exposed AS (\n  SELECT DISTINCT customer_id, variant\n  FROM experiment_exposures WHERE experiment='checkout_v2'\n),\nsucceeded AS (\n  SELECT DISTINCT e.variant, c.customer_id\n  FROM charges c JOIN exposed e USING (customer_id)\n  WHERE c.status='succeeded'\n)\nSELECT e.variant,\n       COUNT(DISTINCT e.customer_id) AS exposed,\n       COUNT(DISTINCT s.customer_id) AS succeeded,\n       ROUND(COUNT(DISTINCT s.customer_id)::numeric\n             / NULLIF(COUNT(DISTINCT e.customer_id),0), 4) AS success_from_exposure\nFROM exposed e\nLEFT JOIN succeeded s ON s.variant = e.variant AND s.customer_id = e.customer_id\nGROUP BY e.variant\nORDER BY e.variant;",
    "verify": {
      "grain": "One row per variant.",
      "columns": ["variant", "exposed", "succeeded", "success_from_exposure"],
      "commonWrong": ["Joining succeeded without matching the variant, mixing variants.", "Counting charge events per variant."],
      "validation": ["succeeded ≤ exposed per variant", "rate ∈ [0,1]"],
      "edgeCases": ["A customer exposed to both variants (contamination) — attribute by first exposure or exclude; state it."],
      "checklist": ["variant carried through", "distinct per variant", "guarded rate"]
    },
    "confusion": "Match the variant on the join, or a customer's success leaks into the wrong arm.",
    "explain": "State how you keep each step per variant and handle a doubly-exposed customer.",
    "teaches": "A per-variant funnel with a preserved grain.",
    "mode": "SQL"
  },
  {
    "id": "fu6",
    "ladder": "fun",
    "pos": 6,
    "stage": "Debug",
    "lvl": 3,
    "difficulty": "medium",
    "priority": "should",
    "source": "interview-style",
    "module": "m9",
    "title": "Debug: attempts exceed exposed",
    "concept": ["funnel"],
    "obj": ["Customer", "Charge"],
    "metric": "attempt rate",
    "edge": ["events vs entities"],
    "timed": false,
    "est": "6 min",
    "business": "An 'attempt rate' is showing above 100% — the attempted count is larger than the exposed count.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "Diagnose the bug in one sentence, then fix it so attempted counts distinct customers.",
    "broken": "WITH exposed AS (\n  SELECT DISTINCT customer_id FROM experiment_exposures WHERE experiment='checkout_v2'\n)\nSELECT (SELECT COUNT(*) FROM exposed) AS exposed,\n       COUNT(*) AS attempted,\n       ROUND(COUNT(*)::numeric / NULLIF((SELECT COUNT(*) FROM exposed),0), 4) AS attempt_rate\nFROM charges c JOIN exposed e USING (customer_id);",
    "deliverable": "A one-sentence diagnosis + a corrected distinct-customer query.",
    "why": "A rate above 100% is the unmistakable signature of counting events; fixing it is a core funnel skill.",
    "harder": "You must spot that COUNT(*) counts charge rows, then restructure to distinct customers.",
    "before": ["A customer with several charges contributes several rows to COUNT(*)."],
    "hints": [
      "The join multiplies: each exposed customer's charges all appear, so COUNT(*) counts charge events.",
      "Make an attempted CTE of DISTINCT customer_id."
    ],
    "solution": "WITH exposed AS (\n  SELECT DISTINCT customer_id FROM experiment_exposures WHERE experiment='checkout_v2'\n),\nattempted AS (\n  SELECT DISTINCT c.customer_id FROM charges c JOIN exposed e USING (customer_id)\n)\nSELECT (SELECT COUNT(*) FROM exposed)   AS exposed,\n       (SELECT COUNT(*) FROM attempted) AS attempted,\n       ROUND((SELECT COUNT(*) FROM attempted)::numeric\n             / NULLIF((SELECT COUNT(*) FROM exposed),0), 4) AS attempt_rate;",
    "verify": {
      "grain": "A single summary row.",
      "columns": ["exposed", "attempted", "attempt_rate"],
      "commonWrong": ["COUNT(DISTINCT customer_id) inline but still selecting per-charge rows elsewhere.", "Leaving attempt_rate able to exceed 1."],
      "validation": ["attempted ≤ exposed", "attempt_rate ∈ [0,1]"],
      "edgeCases": ["A customer with one charge already worked; the bug only shows with retries."],
      "checklist": ["named events-vs-entities", "distinct attempted CTE", "rate ≤ 1"]
    },
    "confusion": "The tell is attempt_rate > 1 — only possible if you counted events.",
    "explain": "State the events-vs-entities bug before the fix.",
    "teaches": "Diagnosing and fixing event-level over-counting in a funnel.",
    "mode": "SQL"
  },
  {
    "id": "fu7",
    "ladder": "fun",
    "pos": 7,
    "stage": "Edge-case hard",
    "lvl": 4,
    "difficulty": "hard",
    "priority": "stretch",
    "source": "StrataScratch-style",
    "module": "m9",
    "title": "Conversion within a 7-day window of exposure",
    "concept": ["funnel"],
    "obj": ["Customer", "Charge"],
    "metric": "windowed conversion",
    "edge": ["conversion window", "late events", "repeated attempts"],
    "timed": false,
    "est": "10 min",
    "business": "Only count an attempt/success if it happened within 7 days of the customer's exposure — later charges aren't attributable to checkout_v2.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "Build the exposed → attempted → succeeded funnel, but only count charges in [exposed_at, exposed_at + 7 days). Report the counts and both rates.",
    "deliverable": "One row: exposed, attempted, succeeded, attempt_rate, success_from_attempt.",
    "why": "A conversion window is what makes a funnel causal rather than coincidental; getting the time join right is the hard part.",
    "harder": "Adds a per-customer time window to each step and still counts distinct entities.",
    "before": [
      "Take each customer's first exposure: MIN(exposed_at).",
      "An attempt is a charge with created_at within 7 days after that exposure."
    ],
    "howto": [
      "exposed CTE: customer_id, MIN(exposed_at) AS exposed_at for checkout_v2.",
      "attempted: distinct customers with a charge in [exposed_at, exposed_at + 7 days).",
      "succeeded: same window, status='succeeded'."
    ],
    "hints": [
      "Join charges to exposed on customer_id with c.created_at >= e.exposed_at AND c.created_at < e.exposed_at + INTERVAL '7 days'.",
      "Still COUNT DISTINCT customer_id per step."
    ],
    "solution": "WITH exposed AS (\n  SELECT customer_id, MIN(exposed_at) AS exposed_at\n  FROM experiment_exposures WHERE experiment='checkout_v2'\n  GROUP BY customer_id\n),\nattempted AS (\n  SELECT DISTINCT e.customer_id\n  FROM charges c JOIN exposed e ON e.customer_id = c.customer_id\n  WHERE c.created_at >= e.exposed_at\n    AND c.created_at <  e.exposed_at + INTERVAL '7 days'\n),\nsucceeded AS (\n  SELECT DISTINCT e.customer_id\n  FROM charges c JOIN exposed e ON e.customer_id = c.customer_id\n  WHERE c.status='succeeded'\n    AND c.created_at >= e.exposed_at\n    AND c.created_at <  e.exposed_at + INTERVAL '7 days'\n)\nSELECT (SELECT COUNT(*) FROM exposed)   AS exposed,\n       (SELECT COUNT(*) FROM attempted) AS attempted,\n       (SELECT COUNT(*) FROM succeeded) AS succeeded,\n       ROUND((SELECT COUNT(*) FROM attempted)::numeric / NULLIF((SELECT COUNT(*) FROM exposed),0), 4)   AS attempt_rate,\n       ROUND((SELECT COUNT(*) FROM succeeded)::numeric / NULLIF((SELECT COUNT(*) FROM attempted),0), 4) AS success_from_attempt;",
    "verify": {
      "grain": "A single summary row.",
      "columns": ["exposed", "attempted", "succeeded", "attempt_rate", "success_from_attempt"],
      "commonWrong": ["Counting charges before exposure or after the window.", "Using all exposures instead of the first per customer."],
      "validation": ["exposed ≥ attempted ≥ succeeded", "rates ∈ [0,1]", "every counted charge is within the window"],
      "edgeCases": ["A charge exactly at exposed_at + 7 days is excluded (half-open window) — state it.", "A customer exposed twice: take the first exposure."],
      "checklist": ["first exposure per customer", "half-open 7-day window", "distinct per step", "guards"]
    },
    "confusion": "Use the first exposure and a half-open window so a charge counts for exactly one window edge.",
    "explain": "Justify the window bounds and first-exposure choice aloud.",
    "teaches": "Attributing conversions to a per-entity exposure window.",
    "mode": "SQL"
  },
  {
    "id": "fu8",
    "ladder": "fun",
    "pos": 8,
    "stage": "Final boss",
    "lvl": 5,
    "difficulty": "final-boss",
    "priority": "boss",
    "source": "Stripe-interview-style",
    "module": "m9",
    "title": "Final boss (timed 12 min): control vs treatment funnel",
    "concept": ["funnel"],
    "obj": ["Customer", "Charge"],
    "metric": "funnel A/B",
    "edge": ["conversion window", "guardrail", "contamination"],
    "timed": true,
    "est": "12 min",
    "business": "\"Did checkout_v2 treatment beat control?\" Build the per-variant funnel and say what would stop you shipping.",
    "schema": ["experiment_exposures", "charges"],
    "prompt": "Per variant, report exposed, attempted, succeeded, attempt_rate (vs exposed) and success_from_attempt (vs attempted), counting only charges within 7 days of first exposure. State your windows and one guardrail you'd check before shipping.",
    "deliverable": "One row per variant: variant, exposed, attempted, succeeded, attempt_rate, success_from_attempt.",
    "why": "This is the assembled experiment funnel: per-variant grain, distinct entities, a conversion window, correct denominators, and the judgment to ask for guardrails.",
    "harder": "Combines per-variant grain, per-customer windows, distinct counting, two denominators and a guardrail caveat under time pressure.",
    "before": [
      "First exposure per (customer, variant).",
      "Each step is distinct customers within the variant and the 7-day window.",
      "A conversion 'win' is not enough — watch refund/dispute rate as a guardrail."
    ],
    "howto": [
      "exposed CTE: customer_id, variant, MIN(exposed_at).",
      "attempted/succeeded CTEs: distinct (variant, customer) with a windowed charge.",
      "LEFT JOIN both to exposed on (variant, customer); GROUP BY variant; guarded rates."
    ],
    "hints": [
      "Carry variant through every CTE and join on (variant, customer_id).",
      "Half-open 7-day window: created_at >= exposed_at AND < exposed_at + INTERVAL '7 days'.",
      "Guardrail: dispute/refund rate of the treatment arm."
    ],
    "solution": "WITH exposed AS (\n  SELECT customer_id, variant, MIN(exposed_at) AS exposed_at\n  FROM experiment_exposures WHERE experiment='checkout_v2'\n  GROUP BY customer_id, variant\n),\nattempted AS (\n  SELECT DISTINCT e.variant, e.customer_id\n  FROM charges c\n  JOIN exposed e ON e.customer_id = c.customer_id\n  WHERE c.created_at >= e.exposed_at\n    AND c.created_at <  e.exposed_at + INTERVAL '7 days'\n),\nsucceeded AS (\n  SELECT DISTINCT e.variant, e.customer_id\n  FROM charges c\n  JOIN exposed e ON e.customer_id = c.customer_id\n  WHERE c.status='succeeded'\n    AND c.created_at >= e.exposed_at\n    AND c.created_at <  e.exposed_at + INTERVAL '7 days'\n)\nSELECT e.variant,\n       COUNT(DISTINCT e.customer_id) AS exposed,\n       COUNT(DISTINCT a.customer_id) AS attempted,\n       COUNT(DISTINCT s.customer_id) AS succeeded,\n       ROUND(COUNT(DISTINCT a.customer_id)::numeric / NULLIF(COUNT(DISTINCT e.customer_id),0), 4) AS attempt_rate,\n       ROUND(COUNT(DISTINCT s.customer_id)::numeric / NULLIF(COUNT(DISTINCT a.customer_id),0), 4) AS success_from_attempt\nFROM exposed e\nLEFT JOIN attempted a ON a.variant=e.variant AND a.customer_id=e.customer_id\nLEFT JOIN succeeded s ON s.variant=e.variant AND s.customer_id=e.customer_id\nGROUP BY e.variant\nORDER BY e.variant;",
    "rubric": [
      "Per-variant grain preserved through every step",
      "Distinct customers counted, not charge events",
      "Conversion window applied per first exposure (half-open)",
      "attempt_rate vs exposed and success_from_attempt vs attempted, both stated",
      "Guards (NULLIF) on every rate",
      "A guardrail named (refund/dispute rate) before declaring a winner"
    ],
    "verify": {
      "grain": "One row per variant.",
      "columns": ["variant", "exposed", "attempted", "succeeded", "attempt_rate", "success_from_attempt"],
      "sample": { "cols": ["variant", "exposed", "attempted", "succeeded", "attempt_rate", "success_from_attempt"], "rows": [["control", "5000", "3400", "2700", "0.6800", "0.7941"], ["treatment", "5000", "3650", "2960", "0.7300", "0.8110"]] },
      "commonWrong": ["Counting charge events.", "Letting a doubly-exposed customer's success leak across arms.", "Declaring a winner on conversion without a guardrail."],
      "validation": ["per variant: exposed ≥ attempted ≥ succeeded", "all rates ∈ [0,1]"],
      "edgeCases": ["Contamination (exposed to both arms).", "Late conversions outside the window not counted."],
      "checklist": ["variant grain", "distinct entities", "windowed", "named denominators", "guardrail stated"]
    },
    "confusion": "A higher conversion can still be a bad change if it raises disputes — always pair it with a guardrail.",
    "explain": "Defend the windows, the denominators, and which guardrail you'd check before shipping.",
    "teaches": "A defensible, windowed, per-variant experiment funnel with guardrail judgment.",
    "mode": "SQL"
  }
];
